import React, { useState, useEffect, useRef } from 'react';
import { Badge, Box } from '@mui/joy';
import { Notifications as NotificationsIcon } from '@mui/icons-material';
import { NotificationService } from '../services/notificationService';

interface NotificationBadgeProps {
  count?: number;
  maxCount?: number;
  variant?: 'solid' | 'soft' | 'outlined' | 'plain';
  color?: 'primary' | 'neutral' | 'danger' | 'success' | 'warning';
  className?: string;
  showCount?: boolean;
}

// Global state to prevent multiple instances from making simultaneous requests
let globalNotificationCount: number | null = null;
let globalLastFetch: number = 0;
const FETCH_COOLDOWN = 5000; // 5 seconds cooldown between requests

const NotificationBadge: React.FC<NotificationBadgeProps> = ({
  count: propCount,
  maxCount = 99,
  variant = 'solid',
  color = 'danger',
  className,
  showCount = true
}) => {
  const [count, setCount] = useState<number>(propCount || 0);
  const [loading, setLoading] = useState<boolean>(!propCount);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (propCount !== undefined) {
      setCount(propCount);
      return;
    }

    const fetchCount = async () => {
      // Check if we're still mounted
      if (!mountedRef.current) return;

      // Check cooldown to prevent rate limiting
      const now = Date.now();
      if (now - globalLastFetch < FETCH_COOLDOWN) {
        // Use cached value if available
        if (globalNotificationCount !== null) {
          setCount(globalNotificationCount);
        }
        return;
      }

      try {
        setLoading(true);
        globalLastFetch = now;
        const response = await NotificationService.getNotificationCount();
        
        // Only update if component is still mounted
        if (mountedRef.current) {
          setCount(response.unread);
          globalNotificationCount = response.unread;
        }
      } catch (error) {
        console.error('Error fetching notification count:', error);
        // Use cached value on error
        if (globalNotificationCount !== null && mountedRef.current) {
          setCount(globalNotificationCount);
        }
      } finally {
        if (mountedRef.current) {
          setLoading(false);
        }
      }
    };

    // Initial fetch
    fetchCount();

    // Auto-refresh every 60 seconds (increased from 30 to reduce rate limiting)
    intervalRef.current = setInterval(fetchCount, 60000);
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [propCount]);

  const displayCount = count > maxCount ? `${maxCount}+` : count.toString();

  return (
    <Box className={className}>
      <Badge
        badgeContent={showCount ? displayCount : undefined}
        color={color}
        variant={variant}
        invisible={count === 0}
        sx={{
          '& .MuiBadge-badge': {
            fontSize: '0.75rem',
            height: 'auto',
            minWidth: 'auto',
            padding: '2px 6px'
          }
        }}
      >
        <NotificationsIcon />
      </Badge>
    </Box>
  );
};

export default NotificationBadge; 